import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Button } from '../Button/Button'
import deprecated from '../../utils/deprecated'

/**
 * Bronson FormSection component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-form-section.html
 *
 * @param {React.ReactNode} [children] - The FormSection content.
 * @param {string} [className] - Optional class names.
 * @param {boolean} [defaultOpen=false] - Open the FormSection per default.
 * @param {boolean} [disabled] - Disable the FormSection.
 * @param {boolean} [error] - Render the error variant.
 * @param {React.ReactNode} [headerContent] - Optional header content. Deprecated.
 * @param {string} [linkLabel] - Label for the link.
 * @param {boolean} [noTruncate] - Do not truncate the title if it exceeds the width.
 * @param {function} [onChange] - Callback when the state open changes.
 * @param {function} [onClick] - Callback when the FormSection receives a click event.
 * @param {function} [onLinkClick] - Callback when the FormSection link receives a click event.
 * @param {string} [subtitle] - Optional subtitle content.
 * @param {boolean} [success] - Render the success variant.
 * @param {React.ReactNode} [title] - The FormSection title content.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The FormSection component.
 * @constructor
 */
export function FormSection({
  // disableJsFx,
  children,
  className,
  defaultOpen = false,
  disabled,
  error,
  headerContent,
  linkLabel,
  noTruncate,
  onChange,
  onClick,
  onLinkClick,
  subtitle,
  success,
  title,
  testId,
  ...otherProps
}) {
  const [open, setOpen] = useState(defaultOpen)

  useEffect(() => {
    setOpen(defaultOpen)
  }, [defaultOpen])

  function toggleOpen() {
    setOpen(!open)

    if (onChange) {
      onChange(!open)
    }
  }

  const divClassNameList = classNames(
    'c-form-section',
    'js-form-section',
    'js-is-ready',
    {
      'js-is-fx': false, // !disableJsFx,
      'is-open': open,
      // Convenience prop from Bronson variants.
      'c-form-section--error': error,
      'c-form-section--success': success,
    },
    className
  ).trim()

  const sectionHeaderClassNameList = classNames(
    'c-form-section__header',
    'js-form-section__header',
    {
      'c-form-section__header--no-truncate': noTruncate,
    },
    className
  ).trim()

  const sectionPanelClassNameList = classNames(
    'c-form-section__panel',
    'js-form-section__panel',
    {
      'is-open': open,
    },
    className
  ).trim()

  /**
   * Note: Custom header content is only agreed on by Bronson for specific cases like Customer Portal.
   */
  function renderIfHeaderContent() {
    return headerContent || null
  }

  function renderIfNotHeaderContent() {
    return headerContent ? null : (
      <>
        <span className="c-form-section__title js-form-section__trigger" aria-expanded={open}>
          {/* @TODO: Add `[aria-level]` to element. */}
          {/* eslint-disable-next-line jsx-a11y/role-has-required-aria-props */}
          <span className="c-form-section__title-text" role="heading">
            {title}
          </span>
          {renderIfFormSectionShowLink()}
        </span>
        {renderIfFormSectionSubtitle()}
      </>
    )
  }

  function renderIfFormSectionShowLink() {
    if (linkLabel) {
      return (
        <Button element="span" className="c-form-section__title-link" link small onClick={onLinkClick}>
          {linkLabel}
        </Button>
      )
    }
    return null
  }

  function renderIfFormSectionSubtitle() {
    if (subtitle) {
      return <span className="c-form-section__subtitle">{subtitle}</span>
    }
    return null
  }

  const panelClosedStyle = {
    height: '0px',
    overflowY: 'hidden', // overflow-y in html
    visibility: 'hidden',
  }

  return (
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    <section {...otherProps} data-testid={testId} className={divClassNameList} aria-disabled={disabled}>
      <button
        className={sectionHeaderClassNameList}
        aria-expanded={open}
        onClick={() => !disabled && [toggleOpen(), onClick && onClick()]}
        type="button"
      >
        <span className="c-form-section__header-content">
          {renderIfNotHeaderContent()}
          {renderIfHeaderContent()}
        </span>
        <span className="c-form-section__header-icon" />
      </button>
      <div className={sectionPanelClassNameList} style={open ? {} : panelClosedStyle} aria-hidden={!open}>
        <div className="c-form-section__content">{children}</div>
      </div>
    </section>
  )
}

FormSection.propTypes = {
  // disableJsFx: PropTypes.bool, // Bronson template: 'disable-js-fx'.
  children: PropTypes.node, // Bronson template: 'form-section-content'.
  className: PropTypes.string, // Bronson template: 'form-section-modifier'.
  defaultOpen: PropTypes.bool, // Bronson template: 'form-section-expanded'.
  disabled: PropTypes.bool, // Bronson template: 'disabled'.
  linkLabel: PropTypes.string, // Bronson template: 'form-section-link-label'.
  noTruncate: PropTypes.bool, // Bronson template: 'no-truncate'.
  onChange: PropTypes.func, // Callback when the state open changes.
  onClick: PropTypes.func, // Callback when the FormSection receives a click event.
  onLinkClick: PropTypes.func, // Callback when the FormSection link receives a click event.
  subtitle: PropTypes.string, // Bronson template: 'form-section-subtitle'.
  title: PropTypes.node, // Bronson template: 'form-section-title'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  /**
   * Convenience props.
   */
  error: PropTypes.bool, // Convenience prop for `.c-form-section--error`. Bronson template: 'form-section-modifier'.
  success: PropTypes.bool, // Convenience prop for `.c-form-section--success`. Bronson template: 'form-section-modifier'.
  /**
   * Deprecated props.
   */
  headerContent: deprecated(
    PropTypes.node,
    'Deprecated, Bronson does not allow custom header content anymore. Prop will be removed in Bronson-React v6.0.0.'
  ),
}
